import PropTypes from 'prop-types';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import { fontSize } from '@core-ds/primitives';
import coreComponentsConstants from 'Shared/core-components/constants';

const { color, borderRadius } = coreComponentsConstants;

const DESKTOP = '(min-width: 1001px)';
const MOBILE_TABLET = 'only screen and (max-width: 1000px)';
const MOBILE = 'only screen and (max-width: 599px)';

const BackLinkListElement = styled.div`
   position: relative;
   flex: 1;
   padding-right: 20px;
   overflow: hidden;
   text-overflow: ellipsis;
   min-width: 0;
`;

const BackLink = styled.a`
   display: inline-block;
   font-size: ${fontSize.md};
   line-height: 50px;
   margin-right: auto;
   padding-left: 32px;
   padding-right: 20px;
   transition: background-color 0.1s, color 0.1s;

   @media ${DESKTOP} {
      border-bottom-left-radius: ${borderRadius[2]};
   }

   &:hover {
      background-color: rgba(0, 0, 0, 0.05);
      text-decoration: none;

      .svg-icon {
         opacity: 0.75;
      }
   }
`;

const BackLinkText = styled.span`
   padding-left: 5px;
   color: ${color.gray[5]};
   text-decoration: none;
`;

const backPositionButtonPropTypes = {
   text: PropTypes.string.isRequired,
   mobileText: PropTypes.string.isRequired,
   href: PropTypes.string,
};

/**
 * @param {Object} params
 * @param [params.CustomIcon]
 * @param {string} params.text
 * @param {string} params.mobileText
 * @param {string} [params.href]
 * @param {Function} [params.onClick]
 */
const BackPositionButton = ({ CustomIcon = null, text, mobileText, href, ...props }) => {
   const BackLinkIcon = styled(FontAwesomeIcon)`
      position: absolute;
      bottom: 16px;
      left: 16px;
      pointer-events: none;
   `;

   return (
      <BackLinkListElement {...props}>
         {
            <BackLinkIcon
               icon={faArrowLeft}
               className="svg-icon fa-abs-16"
               color={coreComponentsConstants.color.gray[4]}
            />
         }
         <BackLink href={href} data-testid="back-to-all-button">
            <BackLinkText className="visible-mobile-tablet-inline">{mobileText}</BackLinkText>
            <BackLinkText className="hidden-mobile-tablet">{text}</BackLinkText>
         </BackLink>
      </BackLinkListElement>
   );
};

BackPositionButton.propTypes = backPositionButtonPropTypes;

const ToolbarButtonListItem = styled.div`
   margin: 0 5px;
`;

const PageNavigationButtonBase = styled.button`
   font-size: ${fontSize.md};
   line-height: 30px;
   padding-left: 8px;
   padding-right: 8px;
   color: ${props => props.color};
   background-color: ${props => props.backgroundColor};
   cursor: pointer;

   &:hover {
      background-color: rgba(0, 0, 0, 0.05);
   }

   :visited {
      color: ${props => props.color};
   }

   @media ${MOBILE} {
      padding: 0 5px;
   }
`;

const PageNavigationLink = styled(PageNavigationButtonBase.withComponent('a'))`
   line-height: 50px;

   &:hover {
      text-decoration: none;

      .svg-icon {
         opacity: 0.75;
      }
   }
`;

const PageNavigationButton = styled(PageNavigationButtonBase)`
   border-radius: ${borderRadius[1]};
   border: ${props => (props.includeButtonBorder ? `1px solid ${color.gray[2]}` : 'none')};
`;

const toolbarPositionButtonPropTypes = {
   children: PropTypes.node.isRequired,
};

const ToolbarPositionButton = ({
   children,
   onClick = () => {},
   type = 'submit',
   color = coreComponentsConstants.color.gray[7],
   backgroundColor = coreComponentsConstants.color.white,
   includeButtonBorder = true,
   href = '',
   ...props
}) => {
   let NavigationButton = href === '' ? PageNavigationButton : PageNavigationLink;

   return (
      <ToolbarButtonListItem {...props}>
         <NavigationButton
            color={color}
            backgroundColor={backgroundColor}
            href={href}
            includeButtonBorder={includeButtonBorder}
            onClick={onClick}
            type={type}
         >
            {children}
         </NavigationButton>
      </ToolbarButtonListItem>
   );
};

ToolbarPositionButton.propTypes = toolbarPositionButtonPropTypes;

const NavButtonContainer = styled.div`
   position: relative;
   margin: 0;
   background-color: ${color.gray[1]};
   -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

   @media ${DESKTOP} {
      border-bottom-left-radius: ${borderRadius[2]};
      border-bottom-right-radius: ${borderRadius[2]};
   }

   @media ${MOBILE_TABLET} {
      width: auto;
   }
`;

const PageNavigationBar = styled.div`
   display: flex;
   margin: 0;
   padding-right: 16px;
   height: 50px;
   align-items: center;

   @media ${MOBILE_TABLET} {
      border-radius: 0;
   }
`;

const navBarPropTypes = {
   children: PropTypes.node.isRequired,
};

const NavBar = ({ children, ...props }) => {
   return (
      <div {...props}>
         <NavButtonContainer>
            <PageNavigationBar>{children}</PageNavigationBar>
         </NavButtonContainer>
      </div>
   );
};

NavBar.propTypes = navBarPropTypes;

export { BackPositionButton, ToolbarPositionButton, NavBar };
